import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REQUIRED_KEYS = ["material_s355_eur_kg", "scrap_pct", "cut_setup_eur", "cut_rate_value", "cut_min_eur", "tap_setup_eur", "tap_m8_eur_hole", "markup_pct"];
const PROC_LABEL = { LASER: "LASER", PUNCH: "PUNZONE", EXTERNAL: "ESTERNO" };

export default function RealListWizard() {
  const [meta, setMeta] = useState(null);
  const [rates, setRates] = useState({});
  const [cutUnit, setCutUnit] = useState("EUR_PER_METER");
  const [open, setOpen] = useState(false);
  const [proc, setProc] = useState("");
  const [included, setIncluded] = useState({ surcharge: false, deburr: false, packaging: false, transport: false, order_min: false });
  const prefilled = useRef(false);

  const load = () => api.get("/reader/real-price-list").then((r) => {
    setMeta(r.data);
    const pf = r.data.reconciliation?.prefill;
    if (pf && !prefilled.current) {
      prefilled.current = true;
      setRates((prev) => ({ ...Object.fromEntries(Object.entries(pf).map(([k, v]) => [k, String(v)])), ...prev }));
    }
    if (r.data.last_selection && !proc) setProc(r.data.last_selection.process);
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!meta) return null;

  const rec = meta.reconciliation;
  const optMap = meta.optional_fields || {};
  const labelOf = (k) => meta.fields.find((f) => f.key === k)?.label || k;
  const src = (k) => rec?.sources?.[k];
  const candidates = rec?.conflicts?.[0]?.candidates || [];
  const laserCands = candidates.filter((c) => c.cut_rate_unit === "EUR_PER_MIN");
  const punchCands = candidates.filter((c) => c.cut_rate_unit === "EUR_PER_METER");

  const selectSource = async (process, c) => {
    try {
      const { data } = await api.post("/reader/real-price-list/select-source", { process, candidate_label: c?.label });
      toast.success(`Scelta registrata (evento OPERATOR_PRICE_SOURCE_SELECTED #${data.seq}): ${data.candidate.label}`);
      if (c?.cut_rate_value != null) {
        setRates((p) => ({ ...p, cut_rate_value: String(c.cut_rate_value), ...(c.cut_setup_eur != null ? { cut_setup_eur: String(c.cut_setup_eur) } : {}), ...(c.cut_min_eur != null ? { cut_min_eur: String(c.cut_min_eur) } : {}) }));
        if (c.cut_rate_unit) setCutUnit(c.cut_rate_unit);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    if (!window.confirm("Confermi e attivi come LISTINO REALE (Owner/Admin)? Le voci facoltative non incluse restano NON CONFIGURATE (mai zero).")) return;
    try {
      const { data } = await api.post("/reader/real-price-list/confirm", { rates: { ...rates, cut_rate_unit: cutUnit }, optional_included: included, owner_confirm: true });
      toast.success(`Listino reale confermato — v${data.version} · sha ${data.sha256.slice(0, 10)}…`);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.missing ? `Tariffe mancanti: ${det.missing.join(", ")}` : formatApiErrorDetail(det));
    }
  };

  const Field = ({ k }) => (
    <label className="block">
      <span className="text-[10px] text-muted-foreground">{labelOf(k)}</span>
      <Input type="number" min="0" step="0.01" className="h-7 text-xs" data-testid={`rate-${k}`}
        value={rates[k] ?? ""} onChange={(e) => setRates({ ...rates, [k]: e.target.value })} />
      {src(k) && <span className="block text-[9px] text-emerald-700 leading-tight" data-testid={`src-${k}`}>da {src(k).ref} · {String(src(k).entered_at).slice(0, 10)}</span>}
    </label>
  );

  return (
    <div className="border border-border bg-white mb-4" data-testid="real-list-wizard">
      <div className="px-3 py-2 flex items-center justify-between bg-muted cursor-pointer" onClick={() => setOpen(!open)} data-testid="wizard-toggle">
        <div className="text-sm font-display font-bold">Wizard — Attiva listino reale</div>
        <div className="text-xs">
          {meta.confirmed
            ? <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-900 font-bold" data-testid="real-list-status">LISTINO REALE ATTIVO {meta.last_confirmation ? `· sha ${meta.last_confirmation.sha256.slice(0, 10)}…` : ""}</span>
            : <span className="px-1.5 py-0.5 bg-red-100 text-red-900 font-bold" data-testid="real-list-status">NON ATTIVO — le tariffe demo NON producono preventivi reali</span>}
          <span className="ml-2 text-muted-foreground">{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && (
        <div className="p-3 text-xs space-y-3">
          <p className="text-muted-foreground">{meta.note}</p>

          <div className="border border-emerald-300 bg-emerald-50 p-2" data-testid="section-a">
            <div className="font-bold text-emerald-900 mb-1">A. Dati recuperati (P5A — mai reinserire)</div>
            {rec ? Object.entries(rec.prefill).map(([k, v]) => (
              <div key={k} className="flex flex-wrap gap-x-2" data-testid={`recovered-${k}`}>
                <b>{labelOf(k)}: {String(v).replace(".", ",")}{k === "markup_pct" ? "%" : " €"}</b>
                <span className="text-emerald-800">origine: {src(k)?.ref} · {String(src(k)?.entered_at).slice(0, 10)} · {src(k)?.origin}</span>
              </div>
            )) : <span>nessuna riconciliazione presente</span>}
          </div>

          <div className="border border-amber-300 bg-amber-50 p-2 space-y-2" data-testid="section-b">
            <div className="font-bold text-amber-900">B. Conflitti da risolvere — prima il processo, poi la tariffa coerente (nessuna scelta automatica)</div>
            <div className="w-56">
              <Select value={proc} onValueChange={setProc}>
                <SelectTrigger className="h-7 text-xs bg-white" data-testid="wizard-process"><SelectValue placeholder="Seleziona processo taglio…" /></SelectTrigger>
                <SelectContent>{["LASER", "PUNCH", "EXTERNAL"].map((p) => <SelectItem key={p} value={p}>{PROC_LABEL[p]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {proc === "LASER" && (
              <div className="space-y-1" data-testid="laser-candidates">
                <div>Processo LASER — due candidati NON intercambiabili (1,84 vs 2,00 €/min):</div>
                <div className="flex flex-wrap gap-2">
                  {laserCands.map((c, i) => (
                    <Button key={i} size="sm" variant="outline" className="h-auto py-1 text-[10px] bg-white" onClick={() => selectSource("LASER", c)} data-testid={`laser-candidate-${i}`}>
                      {String(c.cut_rate_value).replace(".", ",")} €/min — {c.label} · {String(c.entered_at).slice(0, 10)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {proc === "PUNCH" && (
              <div className="space-y-1" data-testid="punch-candidates">
                <div>Processo PUNZONE — candidato in <b>€/m</b> (NON è una tariffa laser):</div>
                {punchCands.map((c, i) => (
                  <Button key={i} size="sm" variant="outline" className="h-auto py-1 text-[10px] bg-white" onClick={() => selectSource("PUNCH", c)} data-testid={`punch-candidate-${i}`}>
                    {String(c.cut_rate_value).replace(".", ",")} €/m — {c.label} · {String(c.entered_at).slice(0, 10)}
                  </Button>
                ))}
              </div>
            )}
            {proc === "EXTERNAL" && (
              <div className="space-y-1" data-testid="external-choice">
                <div>Processo ESTERNO — le tariffe interne laser/punzone diventano NOT_APPLICABLE; il costo fornitore si inserisce nel ciclo (tab Lavorazioni).</div>
                <Button size="sm" variant="outline" className="h-auto py-1 text-[10px] bg-white" onClick={() => selectSource("EXTERNAL", null)} data-testid="external-candidate">Registra scelta ESTERNO</Button>
              </div>
            )}
            {meta.last_selection && (
              <div className="text-[10px] text-amber-900" data-testid="last-selection">
                Ultima scelta registrata: <b>{PROC_LABEL[meta.last_selection.process]}</b> — {meta.last_selection.candidate.label} · {meta.last_selection.selected_by_name} · {String(meta.last_selection.at).slice(0, 19)} (evento #{meta.last_selection.seq})
              </div>
            )}
          </div>

          <div className="border border-border p-2" data-testid="section-c">
            <div className="font-bold mb-1">C. Dati obbligatori per il ciclo corrente</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {REQUIRED_KEYS.map((k) => <Field key={k} k={k} />)}
              <label className="block">
                <span className="text-[10px] text-muted-foreground">Unità tariffa taglio</span>
                <Select value={cutUnit} onValueChange={setCutUnit}>
                  <SelectTrigger className="h-7 text-xs" data-testid="rate-cut-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.cut_rate_units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </label>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1" data-testid="route-level-note">
              Restano nel ciclo produttivo (tab Lavorazioni): conferma peso netto 64,210 kg/pz · tempo macchina oppure perimetro di taglio · macchina pressopiega compatibile.
            </div>
          </div>

          <div className="border border-border p-2" data-testid="section-d">
            <div className="font-bold mb-1">D. Voci facoltative — il prezzo diventa obbligatorio SOLO dopo l'attivazione (mai zero automatico)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(optMap).map(([k, flag]) => (
                <div key={k} className="border border-border/60 p-1.5">
                  <label className="flex items-center gap-1 text-[10px] font-bold">
                    <input type="checkbox" data-testid={`opt-toggle-${flag}`} checked={!!included[flag]}
                      onChange={(e) => setIncluded({ ...included, [flag]: e.target.checked })} />
                    Includi nel ciclo
                  </label>
                  <span className="text-[10px] text-muted-foreground">{labelOf(k)}</span>
                  <Input type="number" min="0" step="0.01" className="h-7 text-xs" disabled={!included[flag]} data-testid={`rate-${k}`}
                    value={rates[k] ?? ""} onChange={(e) => setRates({ ...rates, [k]: e.target.value })} />
                  {!included[flag] && <span className="text-[9px] text-sky-700">non inclusa → NOT_APPLICABLE / OPTIONAL_NOT_CONFIGURED</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={confirm} data-testid="confirm-real-list">Conferma e attiva come listino reale</Button>
          </div>
          {meta.last_confirmation && (
            <p className="text-[10px] text-muted-foreground">Ultima conferma: v{meta.last_confirmation.version} · {meta.last_confirmation.confirmed_by_name} · {String(meta.last_confirmation.at).slice(0, 19)} · sha {meta.last_confirmation.sha256}</p>
          )}
        </div>
      )}
    </div>
  );
}
